import { useState } from "react";
import { SelectClienteFinal } from "@/components/ui/SelectClienteFinal";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  useCargaMasivaExcelAprendices,
  useCargaMasivaExcelAprendicesPrograma,
} from "@/hooks/aprendices/useAprendices";
import { toast } from "sonner";
import { useActividadesByPrograma } from '@/hooks/actividadesProgramas/useActividadesByPrograma';
import { XCircle, Download, Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";

export function UploadExcelAprendices(props: {
  setOpen: (open: boolean) => void;
  idPrograma?: number;
  queryKey?: (string | number | undefined)[];
  idClienteFinal?: number;
  actividades?: { id: number; nombre: string }[];
}) {
  const { actividades: actividadesFromProps, setOpen, idPrograma, queryKey = ["aprendices"], idClienteFinal } = props;

  const { data: actividadesFromHook, isLoading: isLoadingActividades } = useActividadesByPrograma(idPrograma);

  const actividades = actividadesFromProps || actividadesFromHook;
  const [clienteFinal, setClienteFinal] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [errores, setErrores] = useState<string[]>([]);
  const { mutate, isPending } = useCargaMasivaExcelAprendices();
  const { mutate: mutatePrograma, isPending: isPendingPrograma } =
    useCargaMasivaExcelAprendicesPrograma();
  const queryClient = useQueryClient();
  const { usuarioInfo } = useCurrentUser();
  const isClienteFinal = usuarioInfo?.rol?.nombre === "CLIENTE";

  const handleSubmit = () => {
    if (!archivo) {
      toast.error("Selecciona un archivo Excel o CSV");
      return;
    }
    // La validación de clienteFinal se hace más abajo con idClienteFinalFinal

    const formData = new FormData();
    formData.append("archivo", archivo);

    const idClienteFinalFinal = idClienteFinal ?? clienteFinal?.value;

    if (!idClienteFinalFinal) {
      toast.error("Selecciona un cliente final para la carga masiva");
      return;
    }

    if (idPrograma) {
      mutatePrograma(
        {
          formData,
          idPrograma,
          idClienteFinal: idClienteFinalFinal,
        },
        {
          onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey });
            toast.success(
              `✅ ${res.exitosos.length} registrados ❌ ${res.errores.length} errores`,
              { duration: 5000 },
            );
            if (res.errores.length === 0) setOpen(false);
            setErrores(res.errores);
          },
          onError: () => toast.error("Error al cargar el archivo"),
        },
      );
    } else {
      mutate(
        { formData, idClienteFinal: idClienteFinalFinal },
        {
          onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey });
            toast.success(
              `✅ ${res.exitosos.length} registrados ❌ ${res.errores.length} errores`,
              { duration: 5000 },
            );
            if (res.errores.length === 0) setOpen(false);
            setErrores(res.errores);
          },
          onError: () => toast.error("Error al cargar el archivo"),
        },
      );
    }
  };

  const handleDownloadTemplate = () => {
    // Create CSV content
    const csvContent = `nombreUsuario,nombre,apellidoPaterno,apellidoMaterno,correo${idPrograma ? ',idUsuario,actividades' : ''}
jperez,Juan,Pérez,García,juan.perez@example.com${idPrograma ? ',1,1' : ''}
mrodriguez,María,Rodríguez,López,maria.rodriguez@example.com${idPrograma ? ',,' : ''}
cgarcia,Carlos,García,Martínez,carlos.garcia@example.com${idPrograma ? ',3,2,4' : ''}`;

    // Create blob and download (BOM \uFEFF ensures Excel reads UTF-8 correctly)
    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "plantilla_usuarios.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Plantilla descargada exitosamente");
  };

  return (
    <div className="space-y-4">
      {/* Info Alert with Template Download */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-900">Formato de archivo</AlertTitle>
        <AlertDescription className="text-blue-800 space-y-2">
          <p className="text-sm">
            El archivo Excel o CSV debe contener las siguientes columnas:
            <br />
            <strong>
              nombreUsuario, nombre, apellidoPaterno, apellidoMaterno, correo
              {idPrograma && <>, idUsuario, actividades</>}
            </strong>
          </p>
          {idPrograma && ( // Show only if idPrograma is present
            <p className="text-sm">
              Las columnas `idUsuario` y `actividades` son opcionales.
              <br />
              `idUsuario`: ID numérico para actualizar un usuario existente en el programa.
              <br />
              `actividades`: IDs de actividades (separados por coma, ej. `1,3,5`). Si no se indica o está vacía, el aprendiz no se asigna a ninguna actividad de inicio.
            </p>
          )}
          <p className="text-xs text-blue-700">
            ⚠️ <strong>Importante:</strong> Ya NO es necesario incluir la
            columna "contraseña". Los usuarios recibirán un correo de activación
            para establecer su propia contraseña.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDownloadTemplate}
            className="mt-2 border-blue-300 text-blue-700 hover:bg-blue-100"
          >
            <Download className="w-4 h-4 mr-2" />
            Descargar plantilla de ejemplo
          </Button>
        </AlertDescription>
      </Alert>

      {idPrograma && actividades && actividades.length > 0 && (
        <Alert className="bg-green-50 border-green-200">
          <Info className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-900">IDs de Actividades del Programa</AlertTitle>
          <AlertDescription className="text-green-800">
            <p className="text-sm mb-2">Usa estos IDs en la columna 'actividades' del archivo:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {actividades.map((act) => (
                <li key={act.id}><strong>{act.id}</strong> - {act.nombre}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Select de cliente final solo si no es carga por programa y no viene idClienteFinal por prop */}
      {!idPrograma && !idClienteFinal && (
        <div>
          <Label className="mb-2.5">Cliente final</Label>
          <SelectClienteFinal
            value={clienteFinal}
            onChange={setClienteFinal}
            placeholder="Selecciona un cliente final"
          />
        </div>
      )}
      <div>
        <Label>Archivo Excel o CSV</Label>
        <div className="flex flex-col gap-2 mt-2.5">
          <label
            htmlFor="archivo-excel-upload-aprendices"
            className="cursor-pointer px-4 py-2 rounded bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200 transition flex items-center gap-2"
          >
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12"
              />
            </svg>
            <span>{archivo ? "Cambiar archivo" : "Selecciona un archivo"}</span>
          </label>
          <input
            id="archivo-excel-upload-aprendices"
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
          />
          {/* Mostrar nombre del archivo seleccionado */}
          {archivo && (
            <span className="text-xs text-gray-500 mt-1">
              Archivo seleccionado: {archivo.name}
            </span>
          )}
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        className="w-full"
        disabled={
          (idPrograma ? isPendingPrograma : isPending) ||
        isLoadingActividades ||
          !archivo ||
          (!idPrograma && !idClienteFinal && !clienteFinal?.value)
        }
        style={
          isClienteFinal && usuarioInfo?.clienteFinal
            ? {
                background: `linear-gradient(90deg, ${
                  usuarioInfo.clienteFinal.colorPrimario || "#1e293b"
                }, ${usuarioInfo.clienteFinal.colorSecundario || "#64748b"})`,
                border: "none",
                color: "white",
              }
            : undefined
        }
      >
        {(idPrograma ? isPendingPrograma : isPending)
          ? "Enviando..."
          : "Registrar aprendices"}
      </Button>

      {errores.length > 0 && (
        <Alert variant="destructive" className="mt-4 max-h-64 overflow-y-auto">
          <AlertTitle>
            <XCircle className="w-5 h-5 inline mr-2" />
            Errores durante el registro:
          </AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {errores.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
